import { BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V1, BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V2 } from "./shared-constants";

export type BlinkoVersion = 'v1' | 'v2';

// Canvas dimensions
export const CANVAS_WIDTH = 1000;
export const CANVAS_HEIGHT = 1000;

// Pin constants
export const PIN_RADIUS_BASE = 8;
export const SPECIAL_PIN_RADIUS_BASE = 16; // 특수 핀은 더 큼
export const PIN_SPACING_X_BASE = 63; // 일반 핀 가로 간격
export const PIN_SPACING_Y_BASE = 55; // 일반 핀 세로 간격
export const SPECIAL_PIN_SPACING_X_BASE = 60; // 특수 핀 가로 간격
export const SPECIAL_PIN_SPACING_Y_BASE = 50; // 특수 핀 세로 간격

// Ball constants
export const BALL_RADIUS_BASE = 8;

// Physics constants (made mutable for runtime tuning)
export const GRAVITY = 0.3;
export const PIN_START = 60;
export const MIN_VELOCITY_THRESHOLD = 0.3; // Minimum velocity to prevent getting stuck
export const STUCK_VELOCITY_BOOST = 0.8; // Boost factor when ball is stuck

// Physics tuning parameters
export const SPAWN_OFFSET_FACTOR = 0.2;
export const SPAWN_HEIGHT_FACTOR = 0.0;
export const INITIAL_VELOCITY_FACTOR = 2.0;
export const NORMAL_PIN_RESTITUTION = 0.5;
export const SPECIAL_PIN_RESTITUTION = 1.0;
export const SPECIAL_PIN_BOOST_FACTOR = 1.5;
// Anti-loop: special-pin boost/elastic applies only for the first N hits per ball; after
// that a special pin acts as a normal (energy-absorbing) pin so the ball settles instead of
// pumping an endless bounce loop. Also caps per-ball special-hit leveling at N. (PROD value.)
export const SPECIAL_PIN_EFFECT_CAP = 6;
export const SPECIAL_PIN_MIN_SPEED_FACTOR = 2;
export const TANGENTIAL_DAMPING = 0.75;
export const COLLISION_COOLDOWN_FRAMES = 2;
export const SEPARATION_OVERLAP = 0.5;
export const MAX_HORIZONTAL_SPEED_FACTOR = 3.0;
export const MAX_VERTICAL_SPEED_FACTOR = 10.0;

// FX constants
export const PIN_SPARK_MAX_FRAMES = 10;

export type LevelConfig = {
  experiencePerLetter: number;
  experiencePerSpecialHit: number;
  experienceToLevelUp: number[];
  maxLevel: number;
}

export const MAX_LEVEL = 13

const LETTERS_EXP = 50
const toLevelConfig = (exps: number[]): LevelConfig => {
  return {
    experiencePerLetter: 10,
    experiencePerSpecialHit: 10,
    experienceToLevelUp: [0, LETTERS_EXP].concat(exps.slice(1, exps.length - 1).map((x) => x * 10 + LETTERS_EXP)),
    maxLevel: MAX_LEVEL,
  }
}

export const LEVEL_CONFIG_V1 = toLevelConfig(BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V1.NORMAL)
export const LEVEL_CONFIG_HARD_V1 = toLevelConfig(BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V1.HARD)
export const LEVEL_CONFIG_V2 = toLevelConfig(BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V2.NORMAL)
export const LEVEL_CONFIG_HARD_V2 = toLevelConfig(BONUS_GAME_SPECIAL_HITS_THRESHOLD_FOR_NEXT_LEVEL_V2.HARD)

// Launches per game (number of user-triggered drops)
export const LAUNCHES_PER_GAME = 10;
export const LETTER_SEED_SALT = 0x1234;
export const MAIN_BALL_SEED_SALT = 0x5678;
export const BONUS_BALL_SEED_SALT = 0xABCD;

// Level multiplier: 2^(n-1)
export const levelToMultiplier = (n: number) => Math.max(1, 2 ** Math.max(0, n - 1));

// Compute 0..1 progress within current level towards next
export const computeLevelProgress = (level: number, experience: number, config: LevelConfig): number => {
  if (level >= config.maxLevel) return 1;
  const prev = config.experienceToLevelUp[Math.max(0, Math.min(level - 1, config.experienceToLevelUp.length - 1))] || 0;
  const next = config.experienceToLevelUp[Math.min(level, config.experienceToLevelUp.length - 1)] || prev;
  const span = Math.max(1, next - prev);
  return Math.max(0, Math.min(1, (experience - prev) / span));
};

// Compute the achieved level from total experience (live)
export const computeReachedLevel = (experience: number, config: LevelConfig): number => {
  let reached = 1;
  for (let i = 0; i < config.experienceToLevelUp.length; i++) {
    const threshold = config.experienceToLevelUp[i];
    if (experience >= threshold) {
      reached = i + 1; // thresholds index i corresponds to level i+1
    }
  }
  return Math.max(1, Math.min(config.maxLevel, reached));
};

// Bin multipliers (REDESIGNED, level-invariant). This is the NORMAL vector: meanBin ~0.699 ->
// body RTP + L13 ceiling ~5748x (RTP 96.40%).
// HARD (reshaped) deliverable bins = these x 1.043 -> RTP 96.13% / ceiling 5994x / CV 15.6.
// The demo RENDERS this base vector for both modes and applies the x1.043 scale to HARD payouts
// in useGameController (see the `hardModeRef` branch). STATS panel shows exact per-mode 10M RTP.
export const BIN_MULTIPLIERS: string[] = ['2', '1.17', '0.4', '0.2', '0.1', '0.1', '0.2', '0.4', '1.17', '2'];

/** Display-only override for bin labels. v3 sets this; v1 leaves it null and is unaffected. */
let displayBinLabels: string[] | null = null;
export const setDisplayBinLabels = (v: string[] | null) => { displayBinLabels = v; };
export const getBinLabel = (i: number) => (displayBinLabels ?? BIN_MULTIPLIERS)[i];

/**
 * Display-only: special-hit count at which a falling ball is drawn as GOLDEN.
 * v3's bonus pays 6x on such balls, so showing it live lets a player watch a ball earn it
 * mid-flight. 0 disables the effect entirely, which is what v1 uses.
 */
let goldenSpecialHits = 0;
export const setGoldenSpecialHits = (n: number) => { goldenSpecialHits = n; };
export const isGoldenBall = (specialHits: number) => goldenSpecialHits > 0 && specialHits >= goldenSpecialHits;

// Mode & letter configuration
export type BlinkoMode = 'main' | 'bonus';
export const BLINKO_LETTERS = ['B', 'O', 'N', 'U', 'S'] as const;
export type BlinkoLetter = typeof BLINKO_LETTERS[number];
export const LETTER_VERTICAL_OFFSET = 30;
export const LETTER_CAPTURE_RADIUS = 12;

export type PhysicsParams = {
  GRAVITY: number;
  INITIAL_VELOCITY_FACTOR: number;
  NORMAL_PIN_RESTITUTION: number;
  SPECIAL_PIN_RESTITUTION: number;
  SPECIAL_PIN_BOOST_FACTOR: number;
  SPECIAL_PIN_MIN_SPEED_FACTOR: number;
  TANGENTIAL_DAMPING: number;
  MAX_HORIZONTAL_SPEED_FACTOR: number;
  MAX_VERTICAL_SPEED_FACTOR: number;
};

// Rendering constants
export const DEVICE_PIXEL_RATIO = 1.5
